import { useTranslation } from 'react-i18next'
import type { MessageAttachment, MessageView } from '@/api/messagesApi'
import type { TicketCsat, TicketResolution } from '@/api/ticketsApi'
import {
  channelOf,
  dateTimeLabel,
  fileBadge,
  formatBytes,
  formatDuration,
  timeLabel,
  type ThreadItem,
} from '@/lib/threadView'
import { cn } from '@/lib/utils'

interface ThreadProps {
  items: ThreadItem[]
  timeZone: string
  /** The ticket's assignee: their avatar is navy inside the yellow block, other agents slate. */
  assigneeId: string | null | undefined
  onDownload?: (attachmentId: string) => void
}

/** Renders the list built by `buildThread()` (artboards 02 and 05). */
export function Thread({ items, timeZone, assigneeId, onDownload }: ThreadProps) {
  return (
    <>
      {items.map((item) => {
        switch (item.kind) {
          case 'day':
            return <DaySeparator key={item.key} label={item.label} />
          case 'message':
            return (
              <MessageItem
                key={item.key}
                message={item.message}
                timeZone={timeZone}
                firstResponseMins={item.firstResponseMins}
                onDownload={onDownload}
              />
            )
          case 'system':
            return <SystemItem key={item.key} time={timeLabel(item.message.createdAt, timeZone)} text={item.message.body} />
          case 'notes':
            return <NotesBlock key={item.key} notes={item.notes} timeZone={timeZone} assigneeId={assigneeId} onDownload={onDownload} />
          case 'resolved':
            return <ResolvedBlock key={item.key} resolution={item.resolution} timeZone={timeZone} />
          case 'csat':
            return <CsatItem key={item.key} csat={item.csat} timeZone={timeZone} />
        }
      })}
    </>
  )
}

/** Artboard 05 legend. */
export function ThreadLegend() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-5 text-label text-ink-muted">
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 shrink-0 rounded-[5px] bg-marker-muted" />
        {t('ticket.legend.customer')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 shrink-0 rounded-[5px] bg-ink" />
        {t('ticket.legend.agent')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 shrink-0 rounded-[2px] border border-note-swatch-border bg-note-swatch" />
        {t('ticket.legend.note')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="font-mono">{t('ticket.legend.systemSample')}</span>
        {t('ticket.legend.system')}
      </span>
    </div>
  )
}

function DaySeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3" role="separator" aria-label={label}>
      <div className="h-px grow bg-line" />
      <span className="text-label font-medium tracking-[0.04em] text-ink-muted">{label}</span>
      <div className="h-px grow bg-line" />
    </div>
  )
}

function Avatar({ initials, tone, size = 'md' }: { initials: string; tone: 'customer' | 'agent' | 'peer'; size?: 'md' | 'sm' }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center font-semibold',
        size === 'md' ? 'size-8 rounded-2xl text-label' : 'size-7 rounded-[14px] text-eyebrow tracking-normal',
        tone === 'customer' ? 'bg-line text-ink-body' : tone === 'agent' ? 'bg-ink text-white' : 'bg-sidebar-avatar text-white',
      )}
    >
      {initials}
    </div>
  )
}

function AttachmentChips({ files, onDownload }: { files: MessageAttachment[]; onDownload?: (id: string) => void }) {
  const { t } = useTranslation()
  if (files.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {files.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => onDownload?.(f.id)}
          title={t('ticket.message.download', { name: f.fileName })}
          className="flex items-center gap-2 rounded-md border border-line-strong px-2.5 py-1.5 text-label hover:bg-surface-subtle"
        >
          <span className="font-mono text-ink-muted">{fileBadge(f.fileName, f.mimeType)}</span>
          <span className="text-ink-strong">{f.fileName}</span>
          <span className="text-ink-faint">{formatBytes(f.sizeBytes)}</span>
        </button>
      ))}
    </div>
  )
}

function MessageItem({
  message,
  timeZone,
  firstResponseMins,
  onDownload,
}: {
  message: MessageView
  timeZone: string
  firstResponseMins: number | null
  onDownload?: (id: string) => void
}) {
  const { t } = useTranslation()
  const channel = channelOf(message)
  const meta = [
    message.author.subtitle,
    timeLabel(message.createdAt, timeZone),
    channel ? t('ticket.message.via', { channel: t(`ticket.message.channel.${channel}`) }) : null,
    firstResponseMins !== null ? t('ticket.message.firstResponse', { duration: formatDuration(firstResponseMins * 60_000) }) : null,
    message.edited ? t('ticket.message.edited') : null,
  ].filter(Boolean)
  const isCustomer = message.author.kind === 'customer'
  return (
    <article className="flex gap-3.5" aria-label={`${message.author.name} ${meta.join(' · ')}`}>
      <Avatar initials={message.author.initials} tone={isCustomer ? 'customer' : 'agent'} />
      <div className={cn('flex min-w-0 grow flex-col', message.attachments.length > 0 ? 'gap-2' : 'gap-1.5')}>
        <div className="flex items-baseline gap-2">
          <span className="text-body font-semibold text-ink-strong">{message.author.name}</span>
          <span className="text-label text-ink-muted">{meta.join(' · ')}</span>
        </div>
        <p className="whitespace-pre-wrap text-[15px] leading-6 text-ink-message">{message.body}</p>
        <AttachmentChips files={message.attachments} onDownload={onDownload} />
      </div>
    </article>
  )
}

function SystemItem({ time, text }: { time: string; text: string }) {
  return (
    <div className="flex items-baseline gap-3.5 pl-[46px]">
      <span className="w-10 shrink-0 font-mono text-label text-ink-muted">{time}</span>
      <span className="text-meta leading-5 text-ink-muted">{text}</span>
    </div>
  )
}

function NotesBlock({
  notes,
  timeZone,
  assigneeId,
  onDownload,
}: {
  notes: MessageView[]
  timeZone: string
  assigneeId: string | null | undefined
  onDownload?: (id: string) => void
}) {
  const { t } = useTranslation()
  return (
    <section
      aria-label={t('ticket.message.internalHeader')}
      className="flex flex-col gap-3.5 rounded-lg border border-note-border bg-note-bg px-[18px] py-4"
    >
      <p className="text-eyebrow font-semibold uppercase text-note-heading">{t('ticket.message.internalHeader')}</p>
      {notes.map((n) => (
        <div key={n.id} className="flex gap-3">
          <Avatar initials={n.author.initials} tone={n.author.id && n.author.id === assigneeId ? 'agent' : 'peer'} size="sm" />
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-baseline gap-2">
              <span className="text-meta font-semibold text-ink-strong">{n.author.name}</span>
              <span className="text-label text-note-meta">
                {[n.author.subtitle, timeLabel(n.createdAt, timeZone)].filter(Boolean).join(' · ')}
              </span>
            </div>
            <p className="whitespace-pre-wrap text-body leading-[22px] text-note-text">{n.body}</p>
            <AttachmentChips files={n.attachments} onDownload={onDownload} />
          </div>
        </div>
      ))}
    </section>
  )
}

/** Artboard 05's green block, without the ESIG code and "Electronically signed" (no e-signature in v1). */
export function ResolvedBlock({ resolution, timeZone }: { resolution: TicketResolution; timeZone: string }) {
  const { t } = useTranslation()
  const tail = [
    t('ticket.resolvedBlock.category', { category: t(`ticket.category.${resolution.category}`, { defaultValue: resolution.category }) }),
    resolution.rootCause ? t('ticket.resolvedBlock.rootCauseRecorded') : null,
    resolution.deviationDecision
      ? t('ticket.resolvedBlock.deviation', {
          decision: t(`ticket.deviation.${resolution.deviationDecision}`, { defaultValue: resolution.deviationDecision }),
        })
      : null,
  ].filter(Boolean)
  const field = (label: string, value: string, mono = false) => (
    <div className="flex flex-col gap-0.5">
      <span className="text-label text-ink-muted">{label}</span>
      <span className={cn('text-ink-strong', mono ? 'font-mono text-meta' : 'text-body font-medium')}>{value}</span>
    </div>
  )
  return (
    <section aria-label={t('ticket.resolvedBlock.heading')} className="flex flex-col gap-3 rounded-lg border border-resolved-border bg-resolved-bg px-5 py-[18px]">
      <p className="text-eyebrow font-semibold uppercase text-resolved-heading">{t('ticket.resolvedBlock.heading')}</p>
      <div className="flex gap-8">
        {field(t('ticket.resolvedBlock.by'), resolution.resolvedBy.name)}
        {field(t('ticket.resolvedBlock.reason'), resolution.reason)}
        {field(t('ticket.resolvedBlock.when'), dateTimeLabel(resolution.resolvedAt, resolution.timeZone || timeZone), true)}
      </div>
      <p className="text-meta leading-5 text-ink-body">{tail.join(' · ')}</p>
    </section>
  )
}

function CsatItem({ csat, timeZone }: { csat: TicketCsat; timeZone: string }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-3.5 pl-[46px]">
      <span className="w-10 shrink-0 pt-0.5 font-mono text-label text-ink-muted">{timeLabel(csat.createdAt, timeZone)}</span>
      <div className="flex flex-col gap-1.5">
        <span className="text-meta text-ink-muted">
          {t('ticket.csat.rated', { name: csat.by ?? t('ticket.csat.someone'), rating: csat.rating })}
        </span>
        {csat.comment ? <q className="text-body italic leading-[22px] text-ink-message">{csat.comment}</q> : null}
      </div>
    </div>
  )
}
